import { For, Show, createMemo, createSignal, onCleanup, onMount } from 'solid-js'

import { createOutsidePointerDown } from '../ui/outsidePointerDown'
import { useI18n } from '../../i18n'
import {
   getHabitTagSummaries,
   habits,
   setHabitsPausedByTag,
   type HabitTagSummary,
} from '../../state/habitsStore'

const ROW_BUTTON_CLASS = 'rounded-control px-3 py-1.5 text-xs font-semibold transition'

/**
 * Renders the two-bar pause icon.
 *
 * @returns The icon element.
 */
const PauseIcon = () => (
   <svg aria-hidden="true" viewBox="0 0 24 24" class="h-5 w-5">
      <path
         d="M8 6v12M16 6v12"
         fill="none"
         stroke="currentColor"
         stroke-linecap="round"
         stroke-width="2.5"
      />
   </svg>
)

/**
 * Renders one tag with its paused count and pause/resume actions. A tag whose
 * tasks are only partly paused offers both.
 *
 * @param props - The tag summary.
 * @returns The row element.
 */
const TagRow = (props: { summary: HabitTagSummary }) => {
   const t = useI18n()
   const allPaused = () => props.summary.paused === props.summary.total
   return (
      <li class="flex items-center gap-2 px-2 py-2">
         <div class="min-w-0 flex-1">
            <p class="truncate text-sm font-semibold text-ink">{props.summary.tag}</p>
            <p class="text-xs text-ink-muted">
               {props.summary.paused > 0 ?
                  t('{paused} of {count} paused', {
                     paused: props.summary.paused,
                     count: props.summary.total,
                  })
               :  t('tag task count', { count: props.summary.total })}
            </p>
         </div>
         <Show when={props.summary.paused > 0}>
            <button
               type="button"
               class={`${ROW_BUTTON_CLASS} bg-emerald-500/20 text-emerald-100 hover:bg-emerald-500/30`}
               onClick={() => setHabitsPausedByTag(props.summary.tag, false)}
            >
               {t('Resume')}
            </button>
         </Show>
         <Show when={!allPaused()}>
            <button
               type="button"
               class={`${ROW_BUTTON_CLASS} border border-amber-300/70 text-amber-100 hover:bg-amber-400/15`}
               onClick={() => setHabitsPausedByTag(props.summary.tag, true)}
            >
               {t('Pause')}
            </button>
         </Show>
      </li>
   )
}

/**
 * Renders the header button that opens a list of tags, each pausing or
 * resuming every task that carries it.
 *
 * @returns The menu element.
 */
const HabitTagPauseMenu = () => {
   const t = useI18n()
   let containerRef: HTMLDivElement | undefined
   const [isOpen, setOpen] = createSignal(false)
   const summaries = createMemo(() => getHabitTagSummaries(habits()))
   const anyTagPaused = createMemo(() => summaries().some(summary => summary.paused > 0))

   createOutsidePointerDown(
      () => containerRef,
      () => setOpen(false),
   )
   onMount(() => {
      /**
       * Closes the menu on Escape.
       *
       * @param event - The keydown event.
       */
      const handleKeyDown = (event: KeyboardEvent) => {
         if (event.key === 'Escape') {
            setOpen(false)
         }
      }
      document.addEventListener('keydown', handleKeyDown)
      onCleanup(() => document.removeEventListener('keydown', handleKeyDown))
   })

   return (
      <div ref={containerRef} class="relative">
         <button
            type="button"
            class="relative grid h-11 w-11 place-items-center rounded-full border border-edge bg-surface-control text-ink-secondary transition hover:bg-surface-control-hover hover:text-ink focus:outline-none focus:ring-2 focus:ring-emerald-200"
            title={t('Pause by tag')}
            aria-label={t('Pause by tag')}
            aria-haspopup="true"
            aria-expanded={isOpen()}
            onClick={() => setOpen(open => !open)}
         >
            <PauseIcon />
            <Show when={anyTagPaused()}>
               <span
                  aria-hidden="true"
                  class="absolute right-0.5 top-0.5 h-2.5 w-2.5 rounded-full bg-amber-400"
               />
            </Show>
         </button>
         <Show when={isOpen()}>
            <div class="absolute right-0 top-full z-20 mt-2 w-72 max-w-[calc(100vw-2rem)] rounded-control border border-edge bg-surface-raised p-2 shadow-xl">
               <h3 class="px-2 pb-1 pt-1 text-sm font-bold text-ink">
                  {t('Pause by tag')}
               </h3>
               <Show
                  when={summaries().length > 0}
                  fallback={
                     <p class="px-2 py-2 text-sm text-ink-muted">
                        {t('Add tags to tasks to pause and resume them together.')}
                     </p>
                  }
               >
                  <ul class="max-h-80 divide-y divide-edge-soft overflow-y-auto">
                     <For each={summaries()}>
                        {summary => <TagRow summary={summary} />}
                     </For>
                  </ul>
               </Show>
            </div>
         </Show>
      </div>
   )
}

export default HabitTagPauseMenu
